// JSON-paste adapter. The caller pastes the contents of receipt.json
// plus optionally an inlined `actions` array, `evidence` map, and
// `attestations` (the SDK never writes this combined shape, but a
// reviewer might assemble one for ad hoc verification).
//
// Accepted shapes:
//
//   1. A bare receipt envelope (receipt.json contents). The verifier
//      will need actions and evidence too; for paste-only flows the
//      envelope shape is sufficient when the envelope already inlines
//      everything (which it does today, since attestations are inline
//      per protocol spec section 3.4). We still need actions.jsonl and
//      evidence/*.json to verify; the loader surfaces a clear error if
//      they are missing.
//
//   2. A "bundle" object with explicit fields:
//      {
//        "receipt": <envelope>,
//        "actions": <ActionRecord[]>,
//        "evidence": { "<action_id>": <EvidenceBlob> }
//      }

import { RECEIPT_SYNTHETIC_ROOT } from "./directory.js";
import type { LoaderResult } from "./types.js";

interface BundleShape {
  receipt: Record<string, unknown>;
  actions: Array<Record<string, unknown>>;
  evidence: Record<string, Record<string, unknown>>;
}

export function loadReceiptFromJson(input: string): LoaderResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch (err) {
    return {
      error: {
        code: "invalid_json",
        message:
          err instanceof Error
            ? `Pasted content is not valid JSON: ${err.message}`
            : "Pasted content is not valid JSON."
      }
    };
  }

  if (looksLikeBundle(parsed)) {
    return loadBundle(parsed as BundleShape);
  }

  if (looksLikeEnvelope(parsed)) {
    return {
      error: {
        code: "envelope_only",
        message:
          "Pasted receipt envelope alone is not sufficient to verify. Paste a bundle with receipt, actions, and evidence, or use the directory drop or ZIP upload to supply the full receipt package."
      }
    };
  }

  return {
    error: {
      code: "unrecognized_shape",
      message: "Pasted JSON did not look like a Sequesign receipt envelope or bundle."
    }
  };
}

function looksLikeEnvelope(v: unknown): v is Record<string, unknown> {
  if (!v || typeof v !== "object") return false;
  const obj = v as Record<string, unknown>;
  return (
    typeof obj.schema_version === "string" && obj.schema_version.startsWith("sequesign.receipt.")
  );
}

function looksLikeBundle(v: unknown): v is BundleShape {
  if (!v || typeof v !== "object") return false;
  const obj = v as Record<string, unknown>;
  return (
    "receipt" in obj &&
    "actions" in obj &&
    "evidence" in obj &&
    looksLikeEnvelope(obj.receipt) &&
    Array.isArray(obj.actions) &&
    obj.evidence !== null &&
    typeof obj.evidence === "object"
  );
}

function loadBundle(bundle: BundleShape): LoaderResult {
  const files = new Map<string, string>();
  files.set("receipt.json", JSON.stringify(bundle.receipt, null, 2));
  files.set("actions.jsonl", bundle.actions.map((a) => JSON.stringify(a)).join("\n") + "\n");
  for (const [actionId, blob] of Object.entries(bundle.evidence)) {
    const safe = actionId.replace(/[^A-Za-z0-9._-]/g, "_");
    files.set(`evidence/${safe}.json`, JSON.stringify(blob, null, 2));
  }
  return {
    receipt: {
      packageDirectory: RECEIPT_SYNTHETIC_ROOT,
      files,
      sourceLabel: "pasted JSON bundle"
    }
  };
}
